<script setup>
  import { onBeforeUnmount, reactive, ref } from 'vue'
  import { useAuthStore } from '@/stores/auth'
  import { ButtonLogin, InputCpf, Modal } from '@/components'
  import authService from '@/services/auth'
  const auth = useAuthStore()
  import govBr from '@/assets/images/login/gov-br.svg'
  import icpBrasil from '@/assets/images/login/ICP-Brasil.png'
  import idRC from '@/assets/idrc/idrc.svg'

  defineProps({
    platform: {
      type: String,
      default: 'home',
    },
    flow: {
      type: String,
      default: 'password',
    },
  })

  const modalLoginApp = ref(null)

  const data = reactive({
    isLoading: false,
    cpf: '',
    intervalVerification: null,
    tries: 0,
  })

  function handleText(element, value) {
    data[element] = value
  }

  async function submit(platform) {
    data.isLoading = true

    try {
      const { interval, expires_in, invalidCpf } = await authService.sendRequestByCIBA(data.cpf)

      if (invalidCpf) {
        data.isLoading = false
      } else {
        await startVerificationAuth(platform, interval * 1000, expires_in * 1000)
      }
    } catch (e) {
      data.isLoading = false
      console.error(e)
    }
  }

  function startVerificationAuth(platform, interval = 10000, expires_in = 30000) {
    data.intervalVerification = setInterval(async function () {
      const response = await authService.verifyAuthByCIBA(platform)
      if (response?.success) {
        stopVerificationAuth()
        auth.redirectLogin(platform)
      } else if (response?.unauthorized) {
        stopVerificationAuth()
      }

      data.tries += interval

      if (data.tries >= expires_in) {
        stopVerificationAuth()
      }
    }, interval)
  }

  function stopVerificationAuth() {
    clearInterval(data.intervalVerification)
    modalLoginApp.value.hide()
  }

  onBeforeUnmount(() => {
    if (data.intervalVerification) {
      clearInterval(data.intervalVerification)
    }
  })
</script>

<template>
  <div class="login-card">
    <div class="login-container">
      <div class="login-header">
        <h1 class="page-title title">Bem vindo!</h1>
        <p>Escolha a forma de realizar o Login.</p>
      </div>
      <div class="login-body">
        <ButtonLogin
          :href="auth.urlLogin(platform, flow)"
          :imgSrc="idRC"
          imgAlt="IdRC - Sistema de Autenticação Eletrônica do Registro Civil"
          text="Entrar com identificação do Registro Civil"
        />
        <ButtonLogin
          :href="auth.urlLogin(platform, 'icp')"
          :imgSrc="icpBrasil"
          imgAlt="ICP - Brasil"
          text="Entrar com certificado digital ICP-Brasil"
        />
        <ButtonLogin
          :href="auth.urlLogin(platform, 'gov.br')"
          :imgSrc="govBr"
          imgAlt="gov.br"
          text="Entrar com gov.br"
        />
      </div>
    </div>
  </div>

  <Modal
    id="modalLoginApp"
    title="Autorização via App"
    ref="modalLoginApp"
    :on-save="() => submit(platform)"
    save-button-text="Encaminhar"
    close-button-text="Cancelar"
    :save-button-disabled="data.isLoading"
    :on-cancel="stopVerificationAuth"
    :close-button-hidden="true"
  >
    <div class="d-flex flex-column align-items-center" style="max-width: 420px">
      <div v-if="!data.isLoading">
        <div class="mb-3 text-center">
          Informe seu CPF para encaminhar solicitação de autorização ao Aplicativo do Registro
          Civil:
        </div>
        <div style="min-width: 320px">
          <InputCpf
            id-field="cpf"
            add-class="mb-3"
            :value-data="data.cpf"
            label-text="CPF"
            :onKey="handleText"
          />
        </div>
      </div>

      <div v-if="data.isLoading" class="mb-3 text-center">
        <div class="mb-3">Aguardando autorização no App do Registro Civil</div>
        <div class="spinner-border text-tertiary" role="status" />
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
  .login-card {
    border-top: 4px solid $secondary;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    margin-bottom: 40px;

    .login-container {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      border-radius: 8px;
      padding: 34px 40px 76px 40px;
      width: 100%;
      box-shadow: $box-shadow;

      .login-header {
        text-align: center;
        padding: 20px 0;

        .page-title {
          color: $secondary;
          font-weight: bolder;
          font-size: 38px;
        }
        p {
          font-size: 20px;
        }
      }
      .login-body {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
      }
    }
  }

  @media (max-width: 576px) {
    .login-container {
      padding: 1rem 0.8rem 2rem 0.8rem !important;

      .login-header {
        .page-title {
          font-size: 24px;
        }

        p {
          font-size: 16px;
        }
      }
      .login-body {
        gap: 0.8rem;
      }
    }
  }
</style>
